import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { User, Mail, Phone, MessageSquare, Loader2, Check } from 'lucide-react';
import { contactFormService } from '../../services/settings/contactFormService';
import { ContactFormData, SpamProtectionData } from '../../services/settings/contact.types';
import clsx from 'clsx';

/**
 * Validation Schema with Zod
 * Multiple honeypot fields with attractive names for bots
 */
const contactFormSchema = z
  .object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    email: z.string().email('Ungültige E-Mail-Adresse').optional().or(z.literal('')),
    phone: z.string().regex(/^\+?[1-9]\d{1,14}$/, 'Ungültige Telefonnummer').optional().or(z.literal('')),
    subject: z.string().min(3, 'Betreff muss mindestens 3 Zeichen lang sein'),
    message: z.string().min(10, 'Nachricht muss mindestens 10 Zeichen lang sein'),
    privacyAccepted: z.boolean().refine((val) => val === true, 'Datenschutzbestimmungen müssen akzeptiert werden'),
    // Multiple honeypot fields
    website: z.string().max(0, 'Spam-Schutz ausgelöst').optional(),
    company_url: z.string().max(0, 'Spam-Schutz ausgelöst').optional(),
    fax_number: z.string().max(0, 'Spam-Schutz ausgelöst').optional(),
  })
  .refine((data) => data.email || data.phone, {
    message: 'Bitte geben Sie eine E-Mail-Adresse oder Telefonnummer an',
    path: ['email'],
  });

type ContactFormSchema = z.infer<typeof contactFormSchema>;

interface ContactFormProps {
  onSuccess?: () => void;
}

// Tailwind Input Styles - eingelassener Look
const inputBaseClasses = `
  w-full h-12 px-4 pl-10 text-base sm:text-[0.95rem]
  bg-[#0d0d0d] text-gray-200 placeholder-[#8a8070]
  border border-white/10 rounded-lg
  shadow-[inset_2px_2px_6px_rgba(0,0,0,0.5),inset_1px_1px_3px_rgba(0,0,0,0.3),inset_-1px_-1px_3px_rgba(255,255,255,0.025)]
  transition-all duration-200
  focus:outline-none focus:border-brand/50
  focus:shadow-[inset_2px_2px_6px_rgba(0,0,0,0.5),inset_1px_1px_3px_rgba(0,0,0,0.3),inset_-1px_-1px_3px_rgba(255,255,255,0.025),inset_0_0_10px_rgba(241,196,15,0.15),inset_0_0_4px_rgba(241,196,15,0.1)]
  disabled:opacity-50 disabled:cursor-not-allowed
`;

const textareaBaseClasses = `
  w-full px-4 py-3 text-base sm:text-[0.95rem] resize-none
  bg-[#0d0d0d] text-gray-200 placeholder-[#8a8070]
  border border-white/10 rounded-lg
  shadow-[inset_2px_2px_6px_rgba(0,0,0,0.5),inset_1px_1px_3px_rgba(0,0,0,0.3),inset_-1px_-1px_3px_rgba(255,255,255,0.025)]
  transition-all duration-200
  focus:outline-none focus:border-brand/50
  focus:shadow-[inset_2px_2px_6px_rgba(0,0,0,0.5),inset_1px_1px_3px_rgba(0,0,0,0.3),inset_-1px_-1px_3px_rgba(255,255,255,0.025),inset_0_0_10px_rgba(241,196,15,0.15),inset_0_0_4px_rgba(241,196,15,0.1)]
  disabled:opacity-50 disabled:cursor-not-allowed
`;

const errorInputClasses = 'border-red-500/50';

export const ContactForm: React.FC<ContactFormProps> = ({ onSuccess }) => {
  const [formLoadTime] = useState(Date.now());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isSuccessHiding, setIsSuccessHiding] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Spam protection state
  const [spamToken, setSpamToken] = useState<SpamProtectionData | null>(null);
  const mouseMovements = useRef(0);
  const keystrokes = useRef(0);
  const formRef = useRef<HTMLFormElement>(null);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid },
  } = useForm<ContactFormSchema>({
    resolver: zodResolver(contactFormSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
      privacyAccepted: false,
      website: '',
      company_url: '',
      fax_number: '',
    },
  });

  // Fetch spam protection token on mount
  useEffect(() => {
    const fetchToken = async () => {
      const token = await contactFormService.getFormToken();
      setSpamToken(token);
    };
    fetchToken();
  }, []);

  // Track mouse movements within form
  const handleMouseMove = useCallback(() => {
    mouseMovements.current += 1;
  }, []);

  // Track keystrokes within form
  const handleKeyDown = useCallback(() => {
    keystrokes.current += 1;
  }, []);

  // Attach event listeners to form
  useEffect(() => {
    const form = formRef.current;
    if (!form) return;

    form.addEventListener('mousemove', handleMouseMove);
    form.addEventListener('keydown', handleKeyDown);

    return () => {
      form.removeEventListener('mousemove', handleMouseMove);
      form.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleMouseMove, handleKeyDown]);

  // Watch all fields for button activation
  const watchedFields = watch();
  const privacyAccepted = watch('privacyAccepted');
  const hasRequiredFields =
    (watchedFields.email || watchedFields.phone) &&
    watchedFields.subject &&
    watchedFields.message &&
    watchedFields.privacyAccepted;

  const onSubmit = async (data: ContactFormSchema) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const formData: ContactFormData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        subject: data.subject,
        message: data.message,
        privacyAccepted: data.privacyAccepted,
        // Honeypot fields
        website: data.website,
        company_url: data.company_url,
        fax_number: data.fax_number,
      };

      const result = await contactFormService.submitContactForm(formData, {
        formToken: spamToken?.formToken,
        challengeAnswer: spamToken?.challengeAnswer,
        mouseMovements: mouseMovements.current,
        keystrokes: keystrokes.current,
        formLoadTime,
      });

      if (result.success) {
        setSubmitSuccess(true);
        reset();

        // Reset tracking
        mouseMovements.current = 0;
        keystrokes.current = 0;

        // Fetch new token for next submission
        const newToken = await contactFormService.getFormToken();
        setSpamToken(newToken);

        if (onSuccess) {
          onSuccess();
        }

        // Nach 5 Sekunden sanft ausblenden
        setTimeout(() => {
          setIsSuccessHiding(true);
          // Nach der Ausblende-Animation entfernen
          setTimeout(() => {
            setSubmitSuccess(false);
            setIsSuccessHiding(false);
          }, 300);
        }, 5000);
      } else {
        setSubmitError(result.error || 'Fehler beim Senden der Nachricht');
      }
    } catch (error) {
      setSubmitError('Fehler beim Senden der Nachricht. Bitte versuchen Sie es später erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isButtonReady = isValid && hasRequiredFields && !isSubmitting;

  return (
    <form ref={formRef} onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
      {/* Autofill override styles */}
      <style>{`
        .tw-contact-input:-webkit-autofill,
        .tw-contact-input:-webkit-autofill:hover,
        .tw-contact-input:-webkit-autofill:focus,
        .tw-contact-input:-webkit-autofill:active,
        .tw-contact-textarea:-webkit-autofill,
        .tw-contact-textarea:-webkit-autofill:hover,
        .tw-contact-textarea:-webkit-autofill:focus,
        .tw-contact-textarea:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px #0d0d0d inset !important;
          -webkit-text-fill-color: #d1d5db !important;
          background-color: #0d0d0d !important;
          caret-color: #d1d5db !important;
        }
        .tw-contact-input, .tw-contact-textarea {
          color-scheme: dark;
        }
      `}</style>

      {/* Error Message */}
      {submitError && (
        <div className="rounded-lg p-4 bg-status-error/10 border border-red-500">
          <p className="text-sm text-status-error">{submitError}</p>
        </div>
      )}

      {/* Name Fields (Optional) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-[#b7ab98] mb-2">
            Vorname (optional)
          </label>
          <div className="relative">
            <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a8070] pointer-events-none z-10" />
            <input
              id="firstName"
              type="text"
              placeholder="Ihr Vorname"
              className={clsx(inputBaseClasses, 'tw-contact-input')}
              {...register('firstName')}
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-[#b7ab98] mb-2">
            Nachname (optional)
          </label>
          <div className="relative">
            <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a8070] pointer-events-none z-10" />
            <input
              id="lastName"
              type="text"
              placeholder="Ihr Nachname"
              className={clsx(inputBaseClasses, 'tw-contact-input')}
              {...register('lastName')}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>

      {/* Email and Phone Fields - Side by side */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-[#b7ab98] mb-2">
            E-Mail-Adresse <span className="text-brand">*</span>
          </label>
          <div className="relative">
            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a8070] pointer-events-none z-10" />
            <input
              id="email"
              type="email"
              placeholder="ihre@email.de"
              className={clsx(inputBaseClasses, 'tw-contact-input', errors.email && errorInputClasses)}
              {...register('email')}
              disabled={isSubmitting}
            />
          </div>
          {errors.email && (
            <p className="mt-1 text-sm text-status-error">{errors.email.message}</p>
          )}
        </div>

        {/* Phone Field */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-[#b7ab98] mb-2">
            Telefonnummer <span className="text-brand">*</span>
          </label>
          <div className="relative">
            <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a8070] pointer-events-none z-10" />
            <input
              id="phone"
              type="tel"
              placeholder="+49 123 456789"
              className={clsx(inputBaseClasses, 'tw-contact-input', errors.phone && errorInputClasses)}
              {...register('phone')}
              disabled={isSubmitting}
            />
          </div>
          {errors.phone && (
            <p className="mt-1 text-sm text-status-error">{errors.phone.message}</p>
          )}
        </div>
      </div>

      {/* Hint text below both fields */}
      <p className="text-xs text-[#8a8070] -mt-4">
        * Mindestens E-Mail oder Telefon erforderlich
      </p>

      {/* Subject Field - Left column only */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-[#b7ab98] mb-2">
            Betreff <span className="text-brand">*</span>
          </label>
          <div className="relative">
            <MessageSquare className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a8070] pointer-events-none z-10" />
            <input
              id="subject"
              type="text"
              placeholder="Worum geht es?"
              className={clsx(inputBaseClasses, 'tw-contact-input', errors.subject && errorInputClasses)}
              {...register('subject')}
              disabled={isSubmitting}
            />
          </div>
          {errors.subject && (
            <p className="mt-1 text-sm text-status-error">{errors.subject.message}</p>
          )}
        </div>
      </div>

      {/* Message Field */}
      <div>
        <label htmlFor="message" className="block text-sm font-medium text-[#b7ab98] mb-2">
          Nachricht <span className="text-brand">*</span>
        </label>
        <textarea
          id="message"
          rows={6}
          placeholder="Ihre Nachricht an uns..."
          className={clsx(textareaBaseClasses, 'tw-contact-textarea', errors.message && errorInputClasses)}
          {...register('message')}
          disabled={isSubmitting}
        />
        {errors.message && (
          <p className="mt-1 text-sm text-status-error">{errors.message.message}</p>
        )}
      </div>

      {/* Honeypot Fields (Hidden) - Multiple fields with attractive names for bots */}
      <div
        aria-hidden="true"
        className="absolute -left-[9999px] w-px h-px overflow-hidden"
      >
        <label htmlFor="website">Website</label>
        <input
          type="text"
          id="website"
          {...register('website')}
          autoComplete="off"
          tabIndex={-1}
        />
        <label htmlFor="company_url">Company URL</label>
        <input
          type="url"
          id="company_url"
          {...register('company_url')}
          autoComplete="off"
          tabIndex={-1}
        />
        <label htmlFor="fax_number">Fax Number</label>
        <input
          type="tel"
          id="fax_number"
          {...register('fax_number')}
          autoComplete="off"
          tabIndex={-1}
        />
      </div>

      {/* Privacy Checkbox - Tailwind Version */}
      <div className="flex flex-col gap-1">
        <label
          className={clsx(
            'flex items-start gap-3 cursor-pointer select-none',
            isSubmitting && 'opacity-50 cursor-not-allowed'
          )}
        >
          {/* Custom Checkbox - Struktur: Input, Box und SVG als Siblings für peer-checked */}
          <div className="relative flex-shrink-0 mt-0.5 w-5 h-5">
            <input
              type="checkbox"
              {...register('privacyAccepted')}
              disabled={isSubmitting}
              className="peer absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            />
            {/* Checkbox Box - Eingelassener Look */}
            <div
              className={clsx(
                'absolute inset-0 w-5 h-5 rounded-md',
                'bg-[#0d0d0d] border border-white/15',
                'shadow-[inset_2px_2px_4px_rgba(0,0,0,0.5),inset_1px_1px_2px_rgba(0,0,0,0.3),inset_-1px_-1px_2px_rgba(255,255,255,0.025)]',
                'transition-all duration-200',
                'peer-focus-visible:border-brand/50',
                'peer-checked:border-brand/40',
                'peer-checked:shadow-[inset_2px_2px_4px_rgba(0,0,0,0.5),inset_1px_1px_2px_rgba(0,0,0,0.3),inset_-1px_-1px_2px_rgba(255,255,255,0.025),inset_0_0_8px_rgba(241,196,15,0.15)]',
                errors.privacyAccepted && 'border-red-500/50'
              )}
            />
            {/* Animated Checkmark SVG */}
            <svg
              className={clsx(
                'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
                'w-3.5 h-3.5 pointer-events-none',
                'text-brand transition-opacity duration-200',
                'drop-shadow-[0_0_3px_rgba(241,196,15,0.8)] drop-shadow-[0_0_6px_rgba(241,196,15,0.4)]',
                'opacity-0 peer-checked:opacity-100'
              )}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path
                d="M4 12l5 5 11-11"
                className={clsx(
                  'transition-all duration-300',
                  privacyAccepted
                    ? '[stroke-dasharray:30] [stroke-dashoffset:0]'
                    : '[stroke-dasharray:30] [stroke-dashoffset:30]'
                )}
              />
            </svg>
          </div>

          {/* Label Text */}
          <span className="text-sm text-[#b7ab98] leading-relaxed">
            Ich habe die{' '}
            <a
              href="/datenschutz"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand font-medium underline hover:opacity-80 transition-opacity"
            >
              Datenschutzerklärung
            </a>{' '}
            zur Kenntnis genommen und stimme der Verarbeitung meiner Daten zur Kontaktaufnahme zu.{' '}
            <span className="text-brand">*</span>
          </span>
        </label>
        {errors.privacyAccepted && (
          <p className="text-sm text-status-error ml-8">{errors.privacyAccepted.message}</p>
        )}
      </div>

      {/* Submit Button - Centered with auto width */}
      <div className="flex justify-center">
        <button
          type="submit"
          disabled={!isButtonReady}
          className={clsx(
            'h-12 px-8 font-semibold rounded-lg',
            'inline-flex items-center justify-center gap-2',
            'bg-[#0d0d0d] text-[#b7ab98]',
            'border transition-all duration-200',
            'shadow-[inset_3px_3px_8px_rgba(0,0,0,0.6),inset_1px_1px_4px_rgba(0,0,0,0.4),inset_-2px_-2px_4px_rgba(255,255,255,0.03)]',
            isButtonReady
              ? 'cursor-pointer border-brand/60 shadow-[inset_3px_3px_8px_rgba(0,0,0,0.6),inset_1px_1px_4px_rgba(0,0,0,0.4),inset_-2px_-2px_4px_rgba(255,255,255,0.03),inset_0_0_6px_2px_rgba(241,196,15,0.4),inset_0_0_14px_rgba(241,196,15,0.18)] hover:text-brand'
              : 'cursor-not-allowed border-[#8a8070]/30 text-[#8a8070]'
          )}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Wird gesendet...</span>
            </>
          ) : (
            <span className="transition-colors duration-200">Nachricht senden</span>
          )}
        </button>
      </div>

      {/* Success Notification - appears below button */}
      {submitSuccess && (
        <div
          className={clsx(
            'flex items-center gap-3 p-4 rounded-lg',
            'bg-[#0d0d0d] border border-brand/50',
            'shadow-[inset_2px_2px_6px_rgba(0,0,0,0.5),inset_-1px_-1px_3px_rgba(255,255,255,0.025),inset_0_0_8px_rgba(241,196,15,0.15)]',
            'animate-[slideIn_0.4s_ease-out_forwards]',
            isSuccessHiding && 'animate-[slideOut_0.3s_ease-out_forwards]'
          )}
        >
          <Check className="w-5 h-5 text-brand flex-shrink-0" />
          <p className="font-medium text-gray-200">Nachricht erfolgreich versendet!</p>
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes slideIn {
          0% { opacity: 0; max-height: 0; padding-top: 0; padding-bottom: 0; transform: translateY(-8px); }
          50% { max-height: 80px; padding-top: 1rem; padding-bottom: 1rem; }
          100% { opacity: 1; max-height: 80px; transform: translateY(0); }
        }
        @keyframes slideOut {
          0% { opacity: 1; max-height: 80px; transform: translateY(0); }
          50% { opacity: 0; transform: translateY(-8px); }
          100% { opacity: 0; max-height: 0; padding-top: 0; padding-bottom: 0; }
        }
      `}</style>
    </form>
  );
};
